import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle, FiSave, FiTrash2, FiShield } from 'react-icons/fi';
import './EmailSettings.css';

function EmailSettings({ addToast }) {
  const [formData, setFormData] = useState({
    sender_name: '',
    sender_email: '',
    email_service: 'sendgrid',
    api_key: ''
  });

  const [loading, setLoading] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const toast = addToast || ((msg, type) => alert(msg));

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/email-settings');
      if (response.data.configured) {
        setCurrentSettings(response.data);
        setFormData(prev => ({
          ...prev,
          sender_name: response.data.sender_name || '',
          sender_email: response.data.sender_email || '',
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.sender_name.trim()) {
      toast('Sender name is required', 'error');
      return;
    }
    if (!formData.sender_email.trim()) {
      toast('Sender email is required', 'error');
      return;
    }
    if (!formData.api_key.trim()) {
      toast('SendGrid API key is required', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/email-settings/save', formData);
      toast('Email settings saved successfully', 'success');
      setCurrentSettings(response.data.data);
      setFormData(prev => ({ ...prev, sender_name: '', api_key: '' }));
    } catch (error) {
      toast(error.response?.data?.error || 'Failed to save settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    try {
      setLoading(true);
      await axios.post('/api/email-settings/verify');
      toast('API key verified successfully with SendGrid', 'success');
      setCurrentSettings(prev => ({
        ...prev,
        is_verified: true,
        verified_at: new Date().toISOString()
      }));
    } catch (error) {
      toast(error.response?.data?.error || 'Verification failed. Check your API key.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete email settings? This will stop email campaigns.')) return;
    try {
      setLoading(true);
      await axios.delete('/api/email-settings');
      toast('Email settings removed', 'success');
      setCurrentSettings(null);
    } catch (error) {
      toast(error.response?.data?.error || 'Failed to delete settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Email Settings</h1>
        <p>Configure your SendGrid API credentials for bulk email campaigns</p>
      </div>

      <div className="settings-layout">
        {/* Configuration Form */}
        <div className="settings-card">
          <div className="settings-card-title">Configure Email Service</div>

          <form onSubmit={handleSave} className="settings-form">
            <div className="form-field">
              <label>Organisation / Sender Name <span className="required">*</span></label>
              <input
                type="text"
                name="sender_name"
                placeholder="e.g., My Company"
                value={formData.sender_name}
                onChange={handleChange}
                disabled={loading}
              />
              <small>Appears in the "From" field of outgoing emails</small>
            </div>

            <div className="form-field">
              <label>Sender Email Address <span className="required">*</span></label>
              <input
                type="email"
                name="sender_email"
                placeholder="e.g., campaigns@yourdomain.com"
                value={formData.sender_email}
                onChange={handleChange}
                disabled={loading}
              />
              <small>Must be verified in SendGrid as an authenticated sender</small>
            </div>

            <div className="form-field">
              <label>Email Service</label>
              <select name="email_service" value={formData.email_service} disabled>
                <option value="sendgrid">SendGrid</option>
              </select>
            </div>

            <div className="form-field">
              <label>SendGrid API Key <span className="required">*</span></label>
              <div className="api-key-wrapper">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  name="api_key"
                  placeholder="SG.xxxxxxxxxxxxxxxxxxxx"
                  value={formData.api_key}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="toggle-key-btn"
                  onClick={() => setShowApiKey(v => !v)}
                  tabIndex={-1}
                >
                  {showApiKey ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              <small>
                Get your key from{' '}
                <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer">
                  SendGrid Dashboard
                </a>
              </small>
            </div>

            <div className="setup-steps">
              <div className="setup-steps-title">Setup Instructions</div>
              <ol>
                <li>Get API key from SendGrid → Settings → API Keys</li>
                <li>Go to Sender Authentication and verify your sender email address</li>
                <li>Add SPF &amp; DKIM DNS records to your domain registrar</li>
                <li>Wait ~30 mins for DNS propagation, then paste your key below</li>
                <li>Click <strong>Save Settings</strong>, then <strong>Verify</strong></li>
              </ol>
            </div>

            <div className="settings-form-actions">
              <button
                type="submit"
                className="btn-form-primary"
                disabled={loading || !formData.sender_name || !formData.sender_email || !formData.api_key}
              >
                <FiSave size={15} />
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>

        {/* Current Settings */}
        <div className="settings-card">
          <div className="settings-card-title">Current Configuration</div>

          {currentSettings ? (
            <div className="current-config">
              <div className="config-row">
                <span className="config-label">Sender Name</span>
                <span className="config-value">{currentSettings.sender_name}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Sender Email</span>
                <span className="config-value">{currentSettings.sender_email}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Service</span>
                <span className="config-value">{currentSettings.email_service?.toUpperCase()}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Status</span>
                <span className={`status-pill ${currentSettings.is_verified ? 'verified' : 'pending'}`}>
                  {currentSettings.is_verified ? (
                    <><FiCheckCircle size={13} /> Verified</>
                  ) : (
                    <><FiAlertCircle size={13} /> Not Verified</>
                  )}
                </span>
              </div>
              {currentSettings.verified_at && (
                <div className="config-row">
                  <span className="config-label">Verified At</span>
                  <span className="config-value">
                    {new Date(currentSettings.verified_at).toLocaleString()}
                  </span>
                </div>
              )}

              <div className="config-alert">
                {currentSettings.is_verified ? (
                  <div className="alert-success">
                    <FiCheckCircle size={15} />
                    <span>Ready to send campaigns. Create a template and select companies in the Database tab.</span>
                  </div>
                ) : (
                  <div className="alert-warning">
                    <FiAlertCircle size={15} />
                    <span>Verify your API key to start sending campaigns.</span>
                  </div>
                )}
              </div>

              <div className="config-actions">
                <button
                  className="btn-form-success"
                  onClick={handleVerify}
                  disabled={loading || currentSettings.is_verified}
                >
                  <FiShield size={15} />
                  {loading ? 'Verifying...' : currentSettings.is_verified ? 'Verified' : 'Verify API Key'}
                </button>
                <button
                  className="btn-form-danger"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  <FiTrash2 size={15} />
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="no-config">
              <p>No email settings configured yet.</p>
              <p>Fill in the form on the left to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailSettings;
